<template>
  <div>
    <!-- Loading skeleton shown during page transitions -->
    <div v-if="isLoading" class="preview-skeleton">
      <div class="preview-skel-header">
        <div class="skel-block preview-skel-thumb"></div>
        <div class="skel-block preview-skel-title"></div>
      </div>
      <div class="preview-skel-pills">
        <div class="skel-block preview-skel-pill" v-for="n in 5" :key="'pill_'+n"></div>
      </div>
      <div class="preview-skel-events">
        <div class="preview-skel-event" v-for="e in 8" :key="'ev_'+e">
          <div class="preview-skel-event-info">
            <div class="skel-block preview-skel-time"></div>
            <div class="preview-skel-teams">
              <div class="skel-block preview-skel-team"></div>
              <div class="skel-block preview-skel-team" style="width:55%"></div>
            </div>
          </div>
          <div class="preview-skel-odds">
            <div class="skel-block preview-skel-odd" v-for="o in 3" :key="'od_'+o"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Page router: renders the current page view -->
    <component v-else :is="currentView" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import HomeView from '@/views/sport/HomeView.vue'
import HomeViewAmerican from '@/views/sport/HomeViewAmerican.vue'
import HomeViewTerminal from '@/views/sport/HomeViewTerminal.vue'
import PreliveView from '@/views/sport/PreliveView.vue'
import TournamentView from '@/views/sport/TournamentView.vue'
import PrematchDetailView from '@/views/sport/PrematchDetailView.vue'
import LiveDetailPrematchView from '@/views/sport/LiveDetailPrematchView.vue'
import { SPORT_PAGES } from '@/store/modules/sportNavigation.js'

export default {
  name: 'SportCenterRouter',
  components: {
    HomeView,
    HomeViewAmerican,
    HomeViewTerminal,
    PreliveView,
    TournamentView,
    PrematchDetailView,
    LiveDetailPrematchView,
  },
  data() {
    return {
      deepLinkConsumed: false,
    }
  },
  watch: {
    currentView(newView, oldView) {
      if (newView !== oldView) {
        window.parent.postMessage({ type: 'scrollToTop' }, '*')
      }
    },
    getPreview(val) {
      if (val.length > 0) this.deepLinkConsumed = true
    },
    getEventsDetail(val) {
      if (val.length > 0) this.deepLinkConsumed = true
    },
    getEventsLiveDetail(val) {
      if (val && val.length > 0) this.deepLinkConsumed = true
    },
  },
  computed: {
    ...mapGetters({
      'getLoadingPreview': 'odds/getLoadingPreview',
      'getLoadingDetail': 'detailPrematch/getLoadingDetail',
      'getPreview': 'previewPrematch/getPreview',
      'getEventsDetail': 'detailPrematch/getEventsDetail',
      'getEventsLiveDetail': 'odds/getEventsLiveDetail',
      'getSubaction': 'ui/getSubaction',
      'getGameConsole': 'settings/getGameConsole',
      'getAccordionDetailActive': 'ui/getAccordionDetailActive',
    }),
    currentPage() {
      return this.$store.getters['sportNavigation/currentPage']
    },
    isPreviewTransition() {
      return this.getLoadingPreview && this.getPreview.length === 0 && this.getEventsDetail.length === 0
    },
    isDetailTransition() {
      // Skip router-level skeleton when accordion inline mode is active:
      // the component handles its own loading spinner and must NOT be destroyed.
      if (this.getAccordionDetailActive) return false
      // Only show router-level skeleton when loading detail from the home page.
      // When a tournament is already visible, MarketViewDetailV3 handles its own
      // skeleton, so we keep showing the tournament view during the load.
      return this.getLoadingDetail && this.getEventsDetail.length === 0 && this.getPreview.length === 0
    },
    isDeepLinkPending() {
      // Once preview/detail has been shown at least once, the deep-link is consumed:
      // never show the skeleton again even if the user closes the view.
      if (this.deepLinkConsumed) return false
      if (!window.eventId && !window.tournamentId) return false
      return (
        this.getEventsDetail.length === 0 &&
        this.getPreview.length === 0 &&
        (this.getEventsLiveDetail || []).length === 0
      )
    },
    isLoading() {
      return this.isPreviewTransition || this.isDetailTransition || this.isDeepLinkPending
    },
    currentView() {
      switch (this.currentPage) {
        case SPORT_PAGES.TOURNAMENT:      return 'TournamentView'
        case SPORT_PAGES.PREMATCH_DETAIL:
          if (this.getGameConsole === 'terminal') {
            return this.getAccordionDetailActive ? 'HomeViewTerminal' : 'TournamentView'
          }
          return 'PrematchDetailView'
        case SPORT_PAGES.LIVE_DETAIL:     return 'LiveDetailPrematchView'
        default:
          if (this.getSubaction === 'prelive') return 'PreliveView'
          return this.getGameConsole === 'american' ? 'HomeViewAmerican' : this.getGameConsole === 'terminal' ? 'HomeViewTerminal' : 'HomeView'
      }
    },
  },
}
</script>

<style scoped>
@keyframes detail-shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.skel-block {
  background: var(--skel-box-bg);
  background-image: var(--skel-box);
  background-size: 200% 100%;
  animation: detail-shimmer 1.5s infinite linear;
  border-radius: 4px;
}

/* ── Preview skeleton (tournament loading / deep-link initial load) ── */
.preview-skeleton         { padding: 0 0 16px; display: flex; flex-direction: column; gap: 2px; }
.preview-skel-header      { display: flex; align-items: center; gap: 10px; padding: 10px 12px; background: var(--bg-panel); border-radius: 4px; margin-bottom: 2px; }
.preview-skel-thumb       { width: 22px; height: 22px; border-radius: 4px; flex-shrink: 0; }
.preview-skel-title       { height: 12px; width: 45%; }
.preview-skel-pills       { display: flex; gap: 6px; padding: 8px 12px; overflow: hidden; background: var(--skel-bg-alt); }
.preview-skel-pill        { height: 26px; width: 72px; border-radius: 13px; flex-shrink: 0; }
.preview-skel-events      { display: flex; flex-direction: column; gap: 1px; }
.preview-skel-event       { display: flex; align-items: center; gap: 8px; padding: 9px 12px; background: var(--skel-box-bg-2); border-radius: 4px; }
.preview-skel-event-info  { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 0; }
.preview-skel-time        { height: 9px; width: 30px; }
.preview-skel-teams       { display: flex; flex-direction: column; gap: 5px; }
.preview-skel-team        { height: 10px; width: 65%; }
.preview-skel-odds        { display: flex; gap: 4px; flex-shrink: 0; }
.preview-skel-odd         { width: 52px; height: 34px; border-radius: 4px; }
</style>
